import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Phone, Send, Loader2, CalendarHeart } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
    name: "",
    phone: "",
    email: "",
    serviceIdx: 0,
    doctorIdx: 0,
    preferred_date: "",
    message: "",
};

export default function Appointment() {
    const { t } = useLang();
    const [form, setForm] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);
    const today = new Date().toISOString().split("T")[0];

    useEffect(() => {
        const handler = (e) => {
            const idx = ["none", "swapnil", "seema"].indexOf(e.detail);
            if (idx >= 0) setForm((f) => ({ ...f, doctorIdx: idx }));
        };
        window.addEventListener("prefill-doctor", handler);
        return () => window.removeEventListener("prefill-doctor", handler);
    }, []);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            const payload = {
                name: form.name,
                phone: form.phone,
                email: form.email,
                service: t.appt.services[form.serviceIdx],
                doctor: t.appt.doctors[form.doctorIdx],
                preferred_date: form.preferred_date,
                message: form.message,
            };
            const res = await fetch(`${API}/appointments`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (res.status === 429) throw Object.assign(new Error("rate"), { rate: true });
            if (!res.ok || !data.success) throw new Error("fail");
            toast.success(t.appt.toastOk, { description: `${form.name} · ${payload.service}` });
            setForm(EMPTY);
        } catch (err) {
            if (err.rate) {
                toast.error(t.appt.tooMany);
            } else {
                toast.error(t.appt.toastErr, { description: t.appt.toastErrDesc });
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="appointment" className="relative py-24 md:py-32">
            <Toaster position="top-center" richColors />
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 md:px-8 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <Reveal>
                        <Eyebrow>{t.appt.eyebrow}</Eyebrow>
                        <h2 className="mt-4 font-display text-4xl font-semibold leading-[1.08] tracking-tight text-brand-navy md:text-5xl lg:text-6xl">
                            {t.appt.h2a} <em className="italic text-brand-olive">{t.appt.h2b}</em>
                        </h2>
                        <p className="mt-6 max-w-md text-base leading-relaxed text-slate-600">{t.appt.text}</p>
                    </Reveal>

                    <Reveal delay={0.15} className="mt-10 space-y-3">
                        {["+91 7208991091", "+91 7208991092"].map((p, i) => (
                            <a
                                key={p}
                                data-testid={`appointment-call-link-${i + 1}`}
                                href={`tel:+91${p.replace(/\D/g, "")}`}
                                className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4 transition-all duration-300 hover:border-brand-olive"
                            >
                                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-olivelight text-brand-olive transition-colors duration-300 group-hover:bg-brand-olive group-hover:text-white">
                                    <Phone className="h-4 w-4" />
                                </span>
                                <div>
                                    <p className="font-mono text-sm font-semibold text-brand-navy">{p}</p>
                                    <p className="text-xs text-slate-500">{t.appt.deskNote}</p>
                                </div>
                            </a>
                        ))}
                        <div className="flex items-center gap-3 pt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                            <CalendarHeart className="h-4 w-4 text-brand-lime" />
                            hello@signitawellness.in
                        </div>
                    </Reveal>
                </div>

                <Reveal delay={0.1} className="lg:col-span-7">
                    <form
                        data-testid="appointment-enquiry-form"
                        onSubmit={submit}
                        className="rounded-3xl border border-slate-200 bg-white p-8 shadow-card md:p-10"
                    >
                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                            <div>
                                <label htmlFor="appt-name" className="mb-2 block text-xs font-bold uppercase tracking-wider text-brand-navy/70">
                                    {t.appt.nameLabel}
                                </label>
                                <input
                                    id="appt-name"
                                    data-testid="appointment-name-input"
                                    type="text"
                                    required
                                    minLength={2}
                                    value={form.name}
                                    onChange={set("name")}
                                    placeholder={t.appt.namePh}
                                    className="field-input"
                                />
                            </div>
                            <div>
                                <label htmlFor="appt-phone" className="mb-2 block text-xs font-bold uppercase tracking-wider text-brand-navy/70">
                                    {t.appt.phoneLabel}
                                </label>
                                <input
                                    id="appt-phone"
                                    data-testid="appointment-phone-input"
                                    type="tel"
                                    required
                                    pattern="[0-9+ ]{10,15}"
                                    value={form.phone}
                                    onChange={set("phone")}
                                    placeholder={t.appt.phonePh}
                                    className="field-input"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label htmlFor="appt-email" className="mb-2 block text-xs font-bold uppercase tracking-wider text-brand-navy/70">
                                    {t.appt.emailLabel} <span className="font-medium normal-case text-slate-400">{t.appt.optional}</span>
                                </label>
                                <input
                                    id="appt-email"
                                    data-testid="appointment-email-input"
                                    type="email"
                                    value={form.email}
                                    onChange={set("email")}
                                    placeholder="you@example.com"
                                    className="field-input"
                                />
                            </div>
                            <div>
                                <label htmlFor="appt-service" className="mb-2 block text-xs font-bold uppercase tracking-wider text-brand-navy/70">
                                    {t.appt.serviceLabel}
                                </label>
                                <select
                                    id="appt-service"
                                    data-testid="appointment-service-select"
                                    value={form.serviceIdx}
                                    onChange={set("serviceIdx")}
                                    className="field-input appearance-none"
                                >
                                    {t.appt.services.map((s, i) => (
                                        <option key={s} value={i}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="appt-doctor" className="mb-2 block text-xs font-bold uppercase tracking-wider text-brand-navy/70">
                                    {t.appt.doctorLabel}
                                </label>
                                <select
                                    id="appt-doctor"
                                    data-testid="appointment-doctor-select"
                                    value={form.doctorIdx}
                                    onChange={set("doctorIdx")}
                                    className="field-input appearance-none"
                                >
                                    {t.appt.doctors.map((d, i) => (
                                        <option key={d} value={i}>
                                            {d}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label htmlFor="appt-date" className="mb-2 block text-xs font-bold uppercase tracking-wider text-brand-navy/70">
                                    {t.appt.dateLabel} <span className="font-medium normal-case text-slate-400">{t.appt.optional}</span>
                                </label>
                                <input
                                    id="appt-date"
                                    data-testid="appointment-date-input"
                                    type="date"
                                    min={today}
                                    value={form.preferred_date}
                                    onChange={set("preferred_date")}
                                    className="field-input"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label htmlFor="appt-message" className="mb-2 block text-xs font-bold uppercase tracking-wider text-brand-navy/70">
                                    {t.appt.msgLabel} <span className="font-medium normal-case text-slate-400">{t.appt.optional}</span>
                                </label>
                                <textarea
                                    id="appt-message"
                                    data-testid="appointment-message-input"
                                    rows={3}
                                    value={form.message}
                                    onChange={set("message")}
                                    placeholder={t.appt.msgPh}
                                    className="field-input resize-none"
                                />
                            </div>
                        </div>

                        <button
                            data-testid="appointment-submit-button"
                            type="submit"
                            disabled={submitting}
                            className="group mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-brand-navy py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-brand-olive disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {submitting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            )}
                            {submitting ? t.appt.submitting : t.appt.submit}
                        </button>
                    </form>
                </Reveal>
            </div>
        </section>
    );
}
